import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  Signal,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { GridApi, GridOptions } from 'ag-grid-community'

import { take } from 'rxjs'

import { FlxShowModalDirective, FlxSpinnerComponent } from '@flowx/angular-ui-toolkit'
import { ExceptionDetailsModalComponent } from '@flowx/designer/modules/process/pages/exception-details-modal/exception-details-modal.component'
import {
  DefaultActionsColumn,
  FlxResourcesGridComponent,
  getActionsColumnLength,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { ActionsCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/actions-cell/actions-cell.component'
import { FlxDateCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/date-cell.component'
import { ApplicationDefinition } from '@flowx/designer/shared/entities/application.entity'
import { ProcessException } from '@flowx/designer/shared/entities/process-exceptions.types'
import { ProcessExceptionsService } from '@flowx/designer/shared/services/process-exceptions.service'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'

@Component({
  selector: 'app-instance-exceptions',
  templateUrl: './instance-exceptions.component.html',
  styleUrls: ['./instance-exceptions.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FlxResourcesGridComponent,
    FlxShowModalDirective,
    FlxSpinnerComponent,
    ExceptionDetailsModalComponent,
  ],
})
export class InstanceExceptionsComponent implements OnDestroy {
  #destroyRef = inject(DestroyRef)
  #processExceptionsService = inject(ProcessExceptionsService)
  #processInstanceService = inject(ProcessInstanceService)

  processInstanceUuid = input<string>(null)
  retryInProgress$ = this.#processInstanceService.retryInProgress

  selectedExceptionInstance = signal<ProcessException | null>(null)
  showDetailsModal = signal(false)

  gridOptions: Signal<GridOptions> = computed(() => ({
    rowModelType: 'infinite',
    datasource: this.#processExceptionsService.gridDataSource(),
  }))

  actions = [
    {
      icon: 'ph-eye',
      tooltip: 'View details',
      clickAction: (exception: ProcessException): void => this.openExceptionModal(exception),
    },
    {
      icon: 'ph-arrow-clockwise',
      tooltip: 'Retry',
      clickAction: (exception: ProcessException): void => this.retryToken(exception),
    },
  ]

  readonly exceptionsTableColumns = [
    { headerName: 'Source', field: 'formattedSource' },
    { headerName: 'Message', field: 'message' },
    { headerName: 'Type', field: 'formattedType' },
    {
      headerName: 'Timestamp',
      field: 'date',
      cellRenderer: FlxDateCellComponent,
      flex: 0,
      width: 180,
    },
    {
      ...DefaultActionsColumn,
      width: getActionsColumnLength(this.actions.length),
      cellRenderer: ActionsCellComponent<ApplicationDefinition>,
      cellRendererParams: {
        actions: this.actions,
      },
    },
  ]

  constructor() {
    effect(() => {
      this.#processExceptionsService.setInitialQuery({ instanceUuid: this.processInstanceUuid() })
    })
  }

  onGridReady(gridApi: GridApi): void {
    this.#processExceptionsService.setGridApi(gridApi)
  }

  openExceptionModal(exception: ProcessException): void {
    this.selectedExceptionInstance.set(exception)
    this.showDetailsModal.set(true)
  }

  hideDetailsModal(): void {
    this.showDetailsModal.set(false)
  }

  retryToken(exception: ProcessException): void {
    this.#processInstanceService
      .retryProcessing(exception.processInstanceUuid, exception.tokenInstanceUuid)
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe()
  }

  ngOnDestroy(): void {
    this.#processExceptionsService.reset()
  }
}
